import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';

// Components
import Topbar from '../main/Topbar';
import ProjectSidebar from '../../components/ProjectSidebar';
import MembershipFormLayout, { type MembershipTabId } from './components/MembershipFormLayout';
import MembershipInfoTab from './components/MembershipInfoTab';
import MembershipBrandMappingTab from './components/MembershipBrandMappingTab';
import MembershipVoucherSettingTab from './components/MembershipVoucherSettingTab';

// Utils
import { getProjectCode } from '../../utils/auth';
import { ProjectDetail, CommonUtils } from '../../utils/commonUtils';
import {
  datetimeLocalToIsoUtc,
} from '../../utils/dateTime';
import {
  getMembershipDetail,
  getBrandMemberships,
  updateMembership,
} from '../../utils/membership';
import { MembershipFormUtils } from '../../utils/membershipCommon';
import {
  type MembershipFormData,
  type MembershipFormErrors,
  membershipNeedsVoucherTab,
  mapMembershipDetailToFormData,
  buildMembershipUpdateRequest,
  MEMBERSHIP_FIELD_MAPPING,
} from './components/membershipFormTypes';
import { ArrowLeft, AlertCircle } from 'lucide-react';

const VALID_TABS: MembershipTabId[] = ['info', 'brand', 'voucher'];

const MembershipUpdate: React.FC = () => {
  const navigate = useNavigate();
  const { membershipCode: paramMembershipCode } = useParams<{ membershipCode: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const tabParam = searchParams.get('tab');
  const activeTab: MembershipTabId =
    tabParam && (VALID_TABS as readonly string[]).includes(tabParam) ? (tabParam as MembershipTabId) : 'info';

  const setActiveTab = (tab: MembershipTabId) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const [isDesktopSidebarOpen, setIsDesktopSidebarOpen] = useState(true);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isProfileDropdownOpen, setIsProfileDropdownOpen] = useState(false);
  const [activeMenu, setActiveMenu] = useState('membership-list');
  const [membershipCode, setMembershipCode] = useState<string | null>(null);
  const [mappedBrandCount, setMappedBrandCount] = useState(0);
  const [mappedBrandCodes, setMappedBrandCodes] = useState<string[] | null>(null);

  const [formData, setFormData] = useState<MembershipFormData | null>(null);
  const [originalData, setOriginalData] = useState<MembershipFormData | null>(null);
  const [errors, setErrors] = useState<MembershipFormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [projectDetail, setProjectDetail] = useState<ProjectDetail | null>(null);
  const [membershipMetadata, setMembershipMetadata] = useState<{
    createdAt: string;
    createdBy: string;
    updatedAt: string;
    updatedBy: string;
  } | null>(null);

  MembershipFormUtils.useMobileSidebarResize(setIsMobileSidebarOpen);

  useEffect(() => {
    const projectInfo = MembershipFormUtils.loadProjectDetail();
    if (projectInfo) {
      setProjectDetail(projectInfo);
    }
  }, []);

  // 멤버십 상세 로드
  useEffect(() => {
    const load = async () => {
      if (!projectDetail || !paramMembershipCode) return;
      const projectCode = getProjectCode();
      if (!projectCode) {
        setLoadError('프로젝트 코드를 찾을 수 없습니다.');
        setIsLoading(false);
        return;
      }
      try {
        setIsLoading(true);
        setLoadError(null);
        const membership = await getMembershipDetail(projectCode, paramMembershipCode);
        if (!membership) {
          setLoadError('멤버십 정보를 찾을 수 없습니다.');
          setIsLoading(false);
          return;
        }
        setMembershipCode(membership.code);
        const fd = mapMembershipDetailToFormData(membership);
        setFormData(fd);
        setOriginalData({ ...fd });
        setMembershipMetadata({
          createdAt: membership.createdAt || '',
          createdBy: membership.createdBy || '',
          updatedAt: membership.updatedAt || '',
          updatedBy: membership.updatedBy || '',
        });
      } catch (e) {
        setLoadError(CommonUtils.getNetworkErrorMessage(e));
      } finally {
        setIsLoading(false);
      }
    };
    load();
  }, [projectDetail, paramMembershipCode]);

  // 매핑된 브랜드 수 + 브랜드 코드 목록 로드 (브랜드매핑 탭에서 중복 호출 방지)
  useEffect(() => {
    const load = async () => {
      if (!membershipCode) return;
      const projectCode = getProjectCode();
      if (!projectCode) return;
      try {
        const res = await getBrandMemberships(projectCode, membershipCode);
        const codes = (res.brandMemberships ?? []).map((b) => b.brandCode).filter(Boolean);
        setMappedBrandCount(codes.length);
        setMappedBrandCodes(codes);
      } catch {
        setMappedBrandCodes([]);
      }
    };
    load();
  }, [membershipCode]);

  const hasChanges = formData && originalData && JSON.stringify(formData) !== JSON.stringify(originalData);

  const handleInfoSubmit = async (data: MembershipFormData) => {
    const projectCode = getProjectCode();
    if (!projectCode || !membershipCode) return;
    setSubmitError(null);
    try {
      setIsSubmitting(true);
      const req = buildMembershipUpdateRequest(data, datetimeLocalToIsoUtc);

      const result = await updateMembership(projectCode, membershipCode, req);
      if (result.success) {
        setOriginalData({ ...data });
        setSearchParams((prev) => {
          const next = new URLSearchParams(prev);
          next.set('tab', 'brand');
          return next;
        });
      } else {
        setSubmitError(result.message || '멤버십 수정에 실패했습니다.');
      }
    } catch (error: any) {
      if (error?.data?.errors?.error && Array.isArray(error.data.errors.error)) {
        const fieldErrors: MembershipFormErrors = {};
        const fieldMapping = MEMBERSHIP_FIELD_MAPPING;
        const generalMessages: string[] = [];
        error.data.errors.error.forEach((err: any) => {
          if (!err?.message) return;
          const fieldKey = err.target ? fieldMapping[err.target] : undefined;
          if (fieldKey) {
            fieldErrors[fieldKey] = err.message;
          } else {
            generalMessages.push(err.message);
          }
        });
        if (Object.keys(fieldErrors).length > 0) {
          setErrors((prev) => ({ ...prev, ...fieldErrors }));
        }
        if (generalMessages.length > 0) {
          setSubmitError(generalMessages.join('\n'));
        } else if (Object.keys(fieldErrors).length === 0) {
          setSubmitError(CommonUtils.getNetworkErrorMessage(error));
        }
      } else {
        setSubmitError(CommonUtils.getNetworkErrorMessage(error));
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleInfoReset = () => {
    if (originalData) {
      setFormData({ ...originalData });
      setErrors({});
    }
  };

  const handleBrandSaveSuccess = (newBrandCount?: number, newBrandCodes?: string[]) => {
    if (typeof newBrandCount === 'number') setMappedBrandCount(newBrandCount);
    if (Array.isArray(newBrandCodes)) setMappedBrandCodes(newBrandCodes);
    const rewardType = formData?.rewardType ?? '';
    if (membershipNeedsVoucherTab(rewardType)) {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.set('tab', 'voucher');
        return next;
      });
    } else {
      navigate('/memberships');
    }
  };

  const handleVoucherSaveSuccess = () => {
    navigate('/memberships');
  };

  const renderTabContent = () => {
    if (activeTab === 'info' && formData) {
      return (
        <MembershipInfoTab
          mode="update"
          formData={formData}
          setFormData={setFormData as React.Dispatch<React.SetStateAction<MembershipFormData>>}
          errors={errors}
          setErrors={setErrors}
          onSubmit={handleInfoSubmit}
          onReset={handleInfoReset}
          isSubmitting={isSubmitting}
          submitError={submitError}
          submitButtonText="저장 후 브랜드 매핑 진행"
          onBackClick={() => navigate('/memberships')}
          membershipCode={membershipCode || undefined}
          membershipMetadata={membershipMetadata || undefined}
          hasChanges={hasChanges ?? false}
        />
      );
    }
    if (activeTab === 'brand' && membershipCode && getProjectCode()) {
      return (
        <MembershipBrandMappingTab
          projectCode={getProjectCode()!}
          membershipCode={membershipCode}
          membershipType={formData?.type ?? 'REWARD'}
          rewardType={formData?.rewardType ?? 'NONE'}
          parentBrandCodes={mappedBrandCodes}
          onSaveSuccess={handleBrandSaveSuccess}
        />
      );
    }
    if (activeTab === 'voucher' && membershipCode && getProjectCode()) {
      return (
        <MembershipVoucherSettingTab
          projectCode={getProjectCode()!}
          membershipCode={membershipCode}
          stampSetCount={formData?.stampSetCount}
          voucherExchangeMethod={formData?.voucherExchangeMethod}
          parentBrandCodes={mappedBrandCodes}
          onSaveSuccess={handleVoucherSaveSuccess}
        />
      );
    }
    return null;
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4" />
          <p className="text-gray-600">멤버십 정보를 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">오류가 발생했습니다</h3>
          <p className="text-gray-600 mb-6">{loadError}</p>
          <button
            onClick={() => navigate('/memberships')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            멤버십 목록으로
          </button>
        </div>
      </div>
    );
  }

  if (!formData) return null;

  return (
    <div className="h-screen bg-gray-50 flex overflow-hidden">
      {projectDetail && (
        <ProjectSidebar
          isDesktopSidebarOpen={isDesktopSidebarOpen}
          setIsDesktopSidebarOpen={setIsDesktopSidebarOpen}
          isMobileSidebarOpen={isMobileSidebarOpen}
          setIsMobileSidebarOpen={setIsMobileSidebarOpen}
          activeMenu={activeMenu}
          setActiveMenu={setActiveMenu}
          projectDetail={projectDetail}
        />
      )}

      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {projectDetail && (
          <Topbar
            title="멤버십 수정"
            subtitle={projectDetail.name}
            isMobileSidebarOpen={isMobileSidebarOpen}
            setIsMobileSidebarOpen={setIsMobileSidebarOpen}
            isProfileDropdownOpen={isProfileDropdownOpen}
            setIsProfileDropdownOpen={setIsProfileDropdownOpen}
            showMobileLogo={true}
            projectDetail={projectDetail}
          />
        )}

        <main className="flex-1 p-4 sm:p-6 overflow-y-auto">
          <div className="max-w-4xl mx-auto">
            <MembershipFormLayout
              activeTab={activeTab}
              onTabChange={setActiveTab}
              membershipCode={membershipCode}
              rewardType={formData.rewardType}
              mappedBrandCount={mappedBrandCount}
              onBackClick={() => navigate('/memberships')}
            >
              {renderTabContent()}
            </MembershipFormLayout>
          </div>
        </main>
      </div>
    </div>
  );
};

export default MembershipUpdate;
